import {
    Button,
    Container,
    Modal,
    ProductSelection,
    TopBar,
    PopupModal,
    Spinner
} from 'components'
import { Steps } from 'components/steps/Steps'
import { buildFallbackOrdersPayload } from 'helpers/buildFallbackOrdersPayload'
import {
    buildClientUrl,
    capitalizeWord,
    constants,
    findPermission,
    formatPayloadToClientObject,
    formatClientObjectWithOptimisticDiscounts,
    generateUuidv4,
    getConnectUser,
    isClientInfoComplete,
    ordersAreDifferent,
    getCountryInitials,
    findEnrollmentFlowType,
    ENROLLMENT_FLOW_TYPES,
    ENROLLMENT_STEPS,
    getStorage,
    getEnrollmentFlowSteps,
    getStepperDisplay,
    isFullBleedEnrollmentStep,
    permissionFlags,
    shouldShowPackageChoice,
    buildProductListWithAddOns,
    sortProductListWithAddOns,
    updateNbSelectedUnits,
    findAddOnsForSelectedPackages,
    dropAddOnsWithoutParentSelected,
    convertProductPricesToAddOns,
    buildMobilePaymentReturnUrl,
    isPaymentResultForAttempt,
    paymentStartEvent,
    paymentWaitExitEvent,
    shouldOpenPaymentRedirect,
    resolveFullGoalItems
} from 'helpers'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    To,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'

import {
    useAppSelector,
    useBindDispatch,
    useGeolocation,
    useOnChange,
    useAnalytics,
    usePaymentPolling
} from 'hooks'
import { WarningModalContent } from 'components/modal/WarningModalContent'
import { useTranslation } from 'react-i18next'
import {
    ClientDetailsStateType,
    MobileMoneyAmount,
    MobileMoneySelection,
    PacketSelectionPage,
    ProductPriceType,
    SelectedPacket,
    ZoneInfoTemplate,
    PaymentFeedbackMessage,
    ProductWithAddOns,
    ProductInfo,
    AddOnInfo,
    EnrollmentPackageChoice,
    RegisteredPackagesSummary
} from 'components/enrollForm'
import { TextField } from 'components/enrollForm/TextField'
import { PhoneNumber } from '../clientDetails/PhoneNumber'
import { AgeInput } from '../clientDetails/AgeInput'
import { GenderForm } from '../clientDetails/GenderForm'
import { GroupRoleForm } from '../clientDetails/GroupRoleForm'
import { deleteBlankId, fetchBlankIds } from 'helpers/blankIdsHelper'
import { ClientDetailsPayloadInterface, ClientStatus, Order } from 'storeTypes'
import { GenderType } from '../enrollmentTypes'
import {
    buildRegisteredPackageRows,
    sumRegisteredPackageNets
} from 'helpers/buildRegisteredPackageRows'
import { SuccessMessage } from 'components/enrollForm/SuccessMessage'
import { InteractionOutcomes } from 'types/interactionOutcomes'
import { InteractionSources } from 'types/interactionSources'
import { ClientReferrals } from 'pages/referrals/ClientReferrals'
import { useOpenExistingClient } from './useOpenExistingClient'

type EnrollmentBaseClassFlags = {
    isFullBleed: boolean
    isClientRegistration: boolean
    isClientModification: boolean
}

const shouldShowPackageDiscountBar = (formType?: string) =>
    !formType ||
    formType === constants.FORM_TYPES.ENROLLMENT ||
    formType === constants.FORM_TYPES.RE_ENROLLMENT ||
    formType === constants.FORM_TYPES.MODIFICATION

const getEnrollmentSuccessIconCategory = (
    isClientRegistration: boolean,
    isClientModification: boolean
) => {
    if (isClientRegistration) {
        return 'clientRegistration'
    }
    if (isClientModification) {
        return 'clientModification'
    }
    return 'default'
}

const buildEnrollmentBaseClasses = ({
    isFullBleed,
    isClientRegistration,
    isClientModification
}: EnrollmentBaseClassFlags) =>
    [
        'enrollment-base--container',
        isFullBleed && 'enrollment-base--container-full-bleed',
        isClientRegistration && 'enrollment-base--flow-client-registration',
        isClientModification && 'enrollment-base--flow-client-modification'
    ]
        .filter(Boolean)
        .join(' ')

const EnrollmentBase = () => {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const nodeRef = useRef(null)
    // Skip leaves the wait immediately. A later poll result or the timeout
    // must not reopen the payment prompt.
    const paymentSkippedRef = useRef(false)
    const { villageId } = useParams()
    const [searchParams] = useSearchParams()
    const originUrl = searchParams.get('from')
    const farmerIdBlankId = searchParams.get('farmerIdBlankId')
    const nextURL = searchParams.get('next')
    const mobilePaymentFeedback =
        searchParams.get('mobile-payment') || undefined
    const { t } = useTranslation()
    const [animate, setAnimate] = useState(false)
    const [addOnSelection, setAddOnSelection] = useState<AddOnInfo[]>([])
    const [
        hasPreselectedPacketsFromGoalItems,
        setHasPreselectedPacketsFromGoalItems
    ] = useState(false)
    const [
        hasInitializedPreselectedPackets,
        setHasInitializedPreselectedPackets
    ] = useState(false)
    const [currentFlowType, setCurrentFlowType] =
        useState<ENROLLMENT_FLOW_TYPES>(ENROLLMENT_FLOW_TYPES.ENROLLMENT)

    const timerEndCallback = () => {
        if (paymentSkippedRef.current) return
        setShowMobilePaymentSpinner(false)
        setShowMobilePaymentModal(true)
    }

    const { startPollingPayment, stopPollingPayment } =
        usePaymentPolling(timerEndCallback)
    const { trackTimedEvent, trackEvent, resetTimedEvent } = useAnalytics()

    const [activeSteps, setActiveSteps] = useState([1])
    const [selectedProduct, setSelectedProduct] = useState<ProductWithAddOns>()
    const [selectedPackets, setSelectedPackets] = useState<SelectedPacket[]>([])
    const [fullGoalItems, setFullGoalItems] = useState<Order[]>([])
    const [goalItemsFetchFailed, setGoalItemsFetchFailed] = useState(false)
    // Bounded by resolveFullGoalItems timeout — always clears so Continue
    // cannot hang, but blocks submit while owned/discount data is unknown.
    const [goalItemsFetchPending, setGoalItemsFetchPending] = useState(true)
    const [selectedMobileMoneyProvider, setSelectedMobileMoneyProvider] =
        useState('')
    const [paymentAmount, setPaymentAmount] = useState(
        Number(searchParams.get('mm-amount')) || 0
    )
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(
        mobilePaymentFeedback !== undefined
    )
    const [clientCode, setClientCode] = useState('')
    const [showMobilePaymentSpinner, setShowMobilePaymentSpinner] =
        useState(false)
    const [providerPromptOpen, setProviderPromptOpen] = useState(false)
    const formCompletedRef = useRef(false)
    const abandonTrackedRef = useRef(false)
    const enrollmentStartedTrackedRef = useRef(false)
    const paymentOutcomeTrackedRef = useRef(false)
    const paymentAttemptActiveRef = useRef(false)
    const paymentStartTrackedRef = useRef(false)
    const completedWithoutPaymentTrackedRef = useRef(false)
    const activeStepsRef = useRef(activeSteps)
    const visibleFlowStepsRef = useRef<ENROLLMENT_STEPS[]>([])
    const [selectedReferrerOption, setSelectedReferrerOption] = useState<{
        option: string
        key?: number
    }>({ option: '' })
    const [productListWithAddons, setProductListWithAddons] = useState<
        ProductWithAddOns[]
    >([])
    const [zoneProductList, setZoneProductList] = useState<ProductInfo[]>([])

    const {
        showWarningModalHandler,
        navbarHandler,
        clientDetailsEnrollmentHandler,
        updatePackagesActionHandler,
        productListHandler,
        sendInteractionsActionHandler,
        updatePaymentCelebrationDataHandler,
        setLastVisitedClientHandler,
        requestMobilePaymentHandler,
        clearMobilePaymentHandler
    } = useBindDispatch()

    const { showWarningModal } = useAppSelector((state) => state.showWarning)
    const { productListData, preFetchLoading } = useAppSelector(
        (store) => store.enrollment
    )
    const { clientOrders } = useAppSelector((state) => state.clientOrders)
    const {
        loading: mobilePaymentLoading,
        response: mobilePaymentResponse,
        error: mobilePaymentError,
        status: mobilePaymentStatus,
        uuid: mobilePaymentUuid
    } = useAppSelector((store) => store.mobilePayment)
    const blockInitialLoadingRef = useRef(
        mobilePaymentLoading && !mobilePaymentResponse?.redirect_url
    )

    const { country, username, zoneSFID: zone } = getConnectUser()
    const currentClient = getStorage(constants.CLIENT_FOR_ENROLLMENT_STORE)
    const { searchQuery, searchResult } = useAppSelector(
        (state) => state.clientSearch
    )

    useEffect(() => {
        const code = currentClient?.client_code || clientCode
        let cancelled = false
        setGoalItemsFetchPending(true)
        resolveFullGoalItems(code, clientOrders)
            .then((orders) => {
                if (!cancelled) {
                    setGoalItemsFetchFailed(false)
                    setFullGoalItems(orders)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setGoalItemsFetchFailed(true)
                    setFullGoalItems([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setGoalItemsFetchPending(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [currentClient?.client_code, clientCode, clientOrders])

    const showPackageDiscountBar = shouldShowPackageDiscountBar(
        currentClient?.form_type
    )

    const missingClientInfo = useMemo(() => {
        if (
            currentClient?.form_type === constants.FORM_TYPES.RE_ENROLLMENT ||
            currentClient?.form_type === constants.FORM_TYPES.MODIFICATION
        ) {
            return !isClientInfoComplete(currentClient)
        }
        return false
    }, [])

    const { inputText, onChangeHandler } = useOnChange<ClientDetailsStateType>({
        firstname:
            currentClient?.firstname ||
            (searchResult && searchResult.length > 0
                ? ''
                : searchQuery?.firstName),
        lastname:
            currentClient?.lastname ||
            (searchResult && searchResult.length > 0
                ? ''
                : searchQuery?.lastName)
    } as ClientDetailsStateType)

    const [phoneNumberInput, setPhoneNumberInput] = useState(
        currentClient?.phone ||
            (searchResult && searchResult.length > 0
                ? ''
                : searchQuery?.primaryPhone)
    )
    const [selectedAge, setSelectedAge] = useState<number | null | undefined>(
        currentClient?.age || null
    )
    const [selectedGender, setSelectedGender] = useState<GenderType>(
        currentClient?.sex?.toLowerCase() || null
    )
    const [selectedGroupRole, setSelectedGroupRole] = useState<
        Record<string, boolean>
    >({
        familyLeader: currentClient?.head_of_household,
        groupLeader: currentClient?.group_leader
    })

    const { geolocation } = useGeolocation({ permissionRequired: false })

    useEffect(() => {
        activeStepsRef.current = activeSteps
    }, [activeSteps])

    const getEnrollmentAnalyticsBase = () => {
        const formType = currentClient?.form_type || 'enrollment'
        const isModification = formType === 'modification'
        const modificationType = currentClient?.modification || ''
        return isModification ? `${formType} ${modificationType}` : formType
    }

    const getCurrentEnrollmentStep = (): ENROLLMENT_STEPS | undefined =>
        visibleFlowStepsRef.current[activeStepsRef.current.length - 1]

    // Provider return remount (?mobile-payment=) is payment-result only: suppress
    // funnel lifecycle noise. Payment outcome + completed-without-payment stay.
    const isPaymentReturnMount = Boolean(mobilePaymentFeedback)

    const sendAbandonedTrackingEvent = (step?: ENROLLMENT_STEPS) => {
        if (
            isPaymentReturnMount ||
            abandonTrackedRef.current ||
            formCompletedRef.current
        ) {
            return
        }
        abandonTrackedRef.current = true
        const exitStep = step || getCurrentEnrollmentStep()
        trackTimedEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: `${getEnrollmentAnalyticsBase()} ${
                constants.ANALYTICS.ABANDONED
            }`,
            ...(exitStep
                ? {
                      name: `${constants.ANALYTICS.NAMES.ENROLLMENT_EXIT}-${exitStep}`
                  }
                : {})
        })
    }

    const trackPackageChoice = (name: string) => {
        if (isPaymentReturnMount) {
            return
        }
        enrollmentStartedTrackedRef.current = true
        trackEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: constants.ANALYTICS.ACTIONS.ENROLLMENT_PACKAGE_CHOICE,
            name
        })
        resetTimedEvent()
    }

    const trackEnrollmentStartedIfNeeded = () => {
        if (isPaymentReturnMount || enrollmentStartedTrackedRef.current) {
            return
        }
        if (
            shouldShowPackageChoice({
                modification: currentClient?.modification,
                flowType: currentFlowType,
                skipForPreselectedPackets: hasPreselectedPacketsFromGoalItems
            })
        ) {
            return
        }
        enrollmentStartedTrackedRef.current = true
        trackEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: `${getEnrollmentAnalyticsBase()} ${
                constants.ANALYTICS.STARTED
            }`
        })
        resetTimedEvent()
    }

    // `payment start` opens a Matomo payment attempt and is the denominator for
    // the payment rates. It fires when the flow first lands on a mobile money
    // step and again on a request made after an outcome closed the previous
    // attempt (retry after failure), so every success / failure pairs with
    // exactly one start.
    const trackPaymentStart = () => {
        if (paymentStartTrackedRef.current) {
            return
        }
        paymentStartTrackedRef.current = true
        trackEvent(paymentStartEvent())
    }

    // One outcome per attempt; tracking it closes the attempt so a retry opens
    // a new `payment start`.
    const trackPaymentOutcome = (name: string) => {
        if (paymentOutcomeTrackedRef.current) {
            return
        }
        paymentOutcomeTrackedRef.current = true
        paymentStartTrackedRef.current = false
        trackTimedEvent({
            category: constants.ANALYTICS.CATEGORIES.PAYMENT,
            action: constants.ANALYTICS.ACTIONS.MOBILE_MONEY_PAYMENT,
            name
        })
    }

    const trackCompletedWithoutPayment = () => {
        if (completedWithoutPaymentTrackedRef.current) {
            return
        }
        completedWithoutPaymentTrackedRef.current = true
        trackTimedEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: `${getEnrollmentAnalyticsBase()} ${
                constants.ANALYTICS.COMPLETED_WITHOUT_PAYMENT
            }`
        })
    }

    const requestMobilePayment = (
        clientCode: string,
        mobilePaymentProvider: string,
        paymentAmount: number
    ) => {
        paymentSkippedRef.current = false
        setProviderPromptOpen(false)
        clearMobilePaymentHandler()
        paymentAttemptActiveRef.current = true
        paymentOutcomeTrackedRef.current = false
        completedWithoutPaymentTrackedRef.current = false
        trackPaymentStart()
        resetTimedEvent()
        requestMobilePaymentHandler({
            client_code: clientCode,
            country_code: getCountryInitials(country),
            provider: mobilePaymentProvider,
            amount: paymentAmount,
            success_url: buildMobilePaymentReturnUrl(
                window.location.href,
                'success',
                paymentAmount
            ),
            error_url: buildMobilePaymentReturnUrl(
                window.location.href,
                'failure',
                paymentAmount
            )
        })
    }

    useEffect(() => {
        if (productListData?.length && productListData?.length > 0) {
            const zoneProductsInfo = productListData?.find(
                (product: ZoneInfoTemplate) => product.zone_sf_id === zone
            ) as ZoneInfoTemplate | undefined
            setZoneProductList(zoneProductsInfo?.product_list ?? [])
        }
    }, [productListData])

    useEffect(() => {
        if (zoneProductList?.length > 0) {
            setProductListWithAddons(
                buildProductListWithAddOns(zoneProductList, currentClient)
            )
        }
    }, [zoneProductList])

    const isClientRegistration =
        !currentClient &&
        currentFlowType === ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITHOUT_PACKAGES

    const isClientModification =
        currentClient?.modification ===
            constants.CLIENT_MODIFICATION_TYPES.DETAILS &&
        currentClient?.form_type === constants.FORM_TYPES.MODIFICATION

    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const canViewReferralFlow = findPermission(
        userPermissions,
        constants.CAN_VIEW_REFERRALS_ENROLLMENT_FLOW
    )
    const canUseFarmerId = findPermission(
        userPermissions,
        permissionFlags.canUseFarmerId
    )
    const trackPhoneLookup = (outcome: string) => {
        trackEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: constants.ANALYTICS.ACTIONS.PHONE_LOOKUP,
            name: outcome
        })
    }
    const { openExistingClient, existingClientModals } = useOpenExistingClient({
        formCompletedRef,
        pathname,
        canUseFarmerId,
        onOpenedExisting: (source) =>
            trackPhoneLookup(
                source === 'local'
                    ? constants.ANALYTICS.NAMES
                          .PHONE_LOOKUP_OPENED_EXISTING_LOCAL
                    : constants.ANALYTICS.NAMES.PHONE_LOOKUP_OPENED_EXISTING
            ),
        navigate
    })

    const changeActiveSteps = () =>
        setActiveSteps((prevSteps) => [
            ...prevSteps,
            prevSteps[prevSteps.length - 1] + 1
        ])

    const handleLastPaymentStep = (
        clientCode: string,
        mobilePaymentProvider: string | undefined
    ) => {
        if (mobilePaymentProvider) {
            requestMobilePayment(
                clientCode,
                mobilePaymentProvider,
                paymentAmount
            )
        } else {
            setShowSuccessModal(true)
        }
    }

    const formatMobileMoneyInfo = () => {
        return {
            mobileMoneyProvider: selectedMobileMoneyProvider,
            paymentAmount
        }
    }

    const clientSubmissionHandler = (blankId: string) => {
        const clientCode = currentClient?.client_code || blankId
        const uuid = generateUuidv4()
        const formType =
            currentClient?.form_type || constants.FORM_TYPES.ENROLLMENT
        const clientDetailsPayload = {
            ...inputText,
            fullname: `${inputText.firstname} ${inputText.lastname}`,
            phone: phoneNumberInput,
            client_code: clientCode,
            username,
            date: new Date().toISOString(),
            form_type: formType,
            village: villageId,
            village_id: villageId,
            villageName: currentClient?.villageName || '',
            client_status:
                currentClient?.client_status || ClientStatus.POTENTIAL,
            uuid,
            farmerIdBlankId: farmerIdBlankId || blankId,
            sex: selectedGender && capitalizeWord(selectedGender),
            age: selectedAge,
            group_leader: selectedGroupRole.groupLeader,
            head_of_household: selectedGroupRole.familyLeader
        } as Partial<ClientDetailsPayloadInterface>

        if (selectedReferrerOption || inputText?.referrer) {
            clientDetailsPayload.referrer =
                selectedReferrerOption?.option || inputText.referrer
        }
        if (selectedPackets?.length === 0 && !currentClient) {
            // when we enroll the store client_for_enrollment is undefined. that is why we check !currenClient
            sendInteractionsActionHandler({
                client_code: clientCode,
                client_status: ClientStatus.POTENTIAL,
                user_id: username,
                survey_name: constants.CLIENT_REGISTRATION_SURVEY_NAME,
                survey_version: constants.CLIENT_REGISTRATION_SURVEY_VERSION,
                village_id: villageId || '',
                source: InteractionSources.CONNECT
            })

            sendInteractionsActionHandler({
                client_code: clientCode,
                client_status: ClientStatus.POTENTIAL,
                user_id: username,
                survey_name: constants.TARGET_LIST_SURVEY_NAME,
                survey_version: constants.TARGET_LIST_SURVEY_VERSION,
                village_id: villageId || '',
                outcome: InteractionOutcomes.FOLLOW_UP,
                source: InteractionSources.CONNECT
            })
        }

        setClientCode(clientCode)
        if (
            currentClient?.modification !==
                constants.CLIENT_MODIFICATION_TYPES.PACKAGES ||
            (selectedPackets?.length === 0 && !currentClient)
        ) {
            clientDetailsEnrollmentHandler({
                clientDetailsPayload,
                gpsCoords: geolocation
            })
        }
        const formattedClientPayload =
            currentClient?.modification ===
            constants.CLIENT_MODIFICATION_TYPES.DETAILS
                ? { ...currentClient, ...clientDetailsPayload }
                : formatPayloadToClientObject(clientDetailsPayload)

        localStorage.setItem(
            constants.CLIENT_DETAIL_LOCAL_STORE,
            JSON.stringify(formattedClientPayload)
        )
        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...formattedClientPayload,
                ...formatMobileMoneyInfo()
            })
        )
        trackTimedEvent({
            category: constants.ANALYTICS.ACTIONS.ENROLLMENT,
            action: `${formType} ${currentClient?.modification || ''} ${
                constants.ANALYTICS.COMPLETED
            }`
        })

        !currentClient && deleteBlankId(blankId)
    }

    const ordersSubmissionHandler = (blankId?: string) => {
        const clientCode = currentClient?.client_code || blankId
        const isPackageModification =
            currentClient?.modification ===
            constants.CLIENT_MODIFICATION_TYPES.PACKAGES
        const ordersPayload = {
            ...inputText,
            sex: selectedGender && capitalizeWord(selectedGender),
            age: selectedAge,
            group_leader: selectedGroupRole.groupLeader,
            head_of_household: selectedGroupRole.familyLeader,
            phone: phoneNumberInput,
            client_code: clientCode,
            packets: selectedPackets,
            goal_items: isPackageModification ? currentClient?.goal_items : [],
            form_type: currentClient?.form_type || 'enrollment',
            village: currentClient?.village_id || villageId,
            client_status: ClientStatus.PACKAGE_WITHOUT_PAYMENT,
            balance: currentClient?.balance || 0
        }

        const formattedOrders = goalItemsFetchFailed
            ? buildFallbackOrdersPayload(ordersPayload)
            : formatClientObjectWithOptimisticDiscounts(ordersPayload, {
                  fullGoalItems,
                  products: productListWithAddons
              })
        localStorage.setItem(
            constants.CLIENT_DETAIL_LOCAL_STORE,
            JSON.stringify(formattedOrders)
        )
        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...formattedOrders,
                ...formatMobileMoneyInfo()
            })
        )

        if (
            !ordersAreDifferent(
                currentClient?.goal_items?.filter(
                    (o: Order) => !o.delivered && !o.ve_delivered
                ),
                selectedPackets
            )
        ) {
            return
        }

        const isPackageEnrollment =
            !currentClient?.form_type ||
            currentClient?.form_type === constants.FORM_TYPES.ENROLLMENT
        const clientStatus =
            currentClient?.client_status || ClientStatus.POTENTIAL
        const resolvedVillageId = currentClient?.village_id || villageId

        sendInteractionsActionHandler({
            survey_data: { orders: selectedPackets },
            client_code: clientCode,
            client_status: clientStatus,
            user_id: username,
            survey_name: isPackageEnrollment
                ? constants.PACKAGE_ENROLLMENT_SURVEY_NAME
                : constants.PACKAGE_MODIFICATION_SURVEY_NAME,
            survey_version: isPackageEnrollment
                ? constants.PACKAGE_ENROLLMENT_SURVEY_VERSION
                : constants.PACKAGE_MODIFICATION_SURVEY_VERSION,
            village_id: resolvedVillageId,
            source: InteractionSources.CONNECT
        })

        if (isPackageEnrollment) {
            sendInteractionsActionHandler({
                client_code: clientCode,
                client_status: clientStatus,
                user_id: username,
                survey_name: constants.TARGET_LIST_SURVEY_NAME,
                survey_version: constants.TARGET_LIST_SURVEY_VERSION,
                village_id: resolvedVillageId,
                outcome: InteractionOutcomes.ENROLLED,
                source: InteractionSources.CONNECT
            })
        }

        updatePackagesActionHandler({
            clientDetails: formattedOrders,
            existingOrders: currentClient?.goal_items || [],
            newOrders: selectedPackets || []
        })

        if (!isPackageEnrollment) {
            setLastVisitedClientHandler(clientCode)
        }
    }

    const handleSubmit = async (mobilePaymentProvider?: string) => {
        // Prices are unreliable while the goal-item fetch is still in flight.
        // Continue is also disabled during this window (≤ timeout), but guard
        // here so a programmatic call never races through with empty owned.
        if (goalItemsFetchPending) return

        formCompletedRef.current = true
        const blankIdRes =
            farmerIdBlankId ||
            currentClient?.client_code ||
            (await fetchBlankIds()).blank_id.code.toString()

        clientSubmissionHandler(blankIdRes)
        if (selectedPackets.length >= 1) {
            // call order submit handler
            ordersSubmissionHandler(blankIdRes)
        }
        handleLastPaymentStep(blankIdRes, mobilePaymentProvider)
        updatePaymentCelebrationDataHandler({
            client_id: blankIdRes,
            seenCelebration: false
        })
    }

    const packageChoiceParams = {
        modification: currentClient?.modification,
        skipForPreselectedPackets: hasPreselectedPacketsFromGoalItems
    }

    const getFlowSteps = (flowType: ENROLLMENT_FLOW_TYPES) =>
        getEnrollmentFlowSteps(flowType, packageChoiceParams)

    // Referrals is omitted from the rendered flow when the VE lacks access or a
    // client already exists — keep analytics / stepper / back indexing on that
    // same filtered list so activeSteps don't map onto the wrong step id.
    const includesReferralsStep = canViewReferralFlow && !currentClient
    const getVisibleFlowSteps = (flowType: ENROLLMENT_FLOW_TYPES) =>
        getFlowSteps(flowType).filter(
            (step) =>
                step !== ENROLLMENT_STEPS.REFERRALS || includesReferralsStep
        )

    const packageChoiceComponent = () => (
        <EnrollmentPackageChoice
            onAddPackages={() => {
                trackPackageChoice(constants.ANALYTICS.NAMES.PACKAGE_CHOICE_ADD)
                changeActiveSteps()
            }}
            onEnrollWithoutPackages={() => {
                trackPackageChoice(
                    constants.ANALYTICS.NAMES.PACKAGE_CHOICE_WITHOUT
                )
                setSelectedPackets([])
                setHasInitializedPreselectedPackets(true)
                setCurrentFlowType(
                    ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITHOUT_PACKAGES
                )
                changeActiveSteps()
            }}
        />
    )

    const productSelectionComponent = () => {
        return (
            <ProductSelection
                selectedPackets={selectedPackets}
                onProductSelected={(product: ProductWithAddOns) => {
                    setSelectedProduct(product)
                    if (product.isAddOn) {
                        setAddOnSelection(convertProductPricesToAddOns(product))
                    } else {
                        changeActiveSteps()
                    }
                }}
                productListWithAddOns={productListWithAddons}
                onClickHandler={() => {
                    // Skip PACKET_SELECTION → land on REGISTERED_PACKAGES
                    changeActiveSteps()
                    changeActiveSteps()
                }}
                btnText={
                    currentClient?.form_type ===
                    constants.FORM_TYPES.MODIFICATION
                        ? t('subscribe')
                        : t('pay')
                }
            />
        )
    }

    const registeredPackagesComponent = () => {
        const flowSteps = getFlowSteps(currentFlowType)
        const isLastStep =
            flowSteps[flowSteps.length - 1] ===
            ENROLLMENT_STEPS.REGISTERED_PACKAGES

        return (
            <div className="enrollment-step-container enrollment-step-container--quantity">
                <RegisteredPackagesSummary
                    selectedPackets={selectedPackets}
                    fullGoalItems={fullGoalItems}
                    productListWithAddOns={productListWithAddons}
                    discountDataUnavailable={goalItemsFetchFailed}
                    continueLabel={t('continue')}
                    continueDisabled={goalItemsFetchPending}
                    onContinue={() => {
                        if (isLastStep) {
                            handleSubmit()
                        } else {
                            changeActiveSteps()
                        }
                    }}
                />
            </div>
        )
    }

    const mobileMoneyAmountComponent = () => (
        <MobileMoneyAmount
            continueHandler={(amount) => {
                changeActiveSteps()
                setPaymentAmount(amount)
            }}
            t={t}
        />
    )

    const mobileMoneySelectionComponent = () => (
        <MobileMoneySelection
            mobileMoneyHandler={(selectedMobilePayment) => {
                setSelectedMobileMoneyProvider(selectedMobilePayment)
            }}
            t={t}
        />
    )

    const resolveTargetedProduct = (sortAddon: boolean) => {
        if (!selectedProduct) return undefined
        if (!sortAddon || selectedProduct.isAddOn) return selectedProduct
        return productListWithAddons.find(
            (product) => product.sf_id === selectedProduct.addOns[0].product_id
        )
    }

    const resolveMatchingAddOns = (
        targetedProduct: ProductWithAddOns,
        updatedProductList: ProductWithAddOns[],
        sortAddon: boolean
    ): AddOnInfo[] => {
        const hasUnitSelected = updatedProductList.some(
            (product) =>
                targetedProduct.sf_id === product.sf_id &&
                product.nbSelectedUnits > 0
        )
        if (!hasUnitSelected || sortAddon || addOnSelection.length > 0) {
            return []
        }
        return findAddOnsForSelectedPackages(targetedProduct, selectedPackets)
    }

    const handlePacketConfirmation = (sortAddon = false) => {
        const targetedProduct = resolveTargetedProduct(sortAddon)
        if (!targetedProduct) return

        let updatedProductList = updateNbSelectedUnits(
            productListWithAddons,
            selectedPackets
        )
        const matchingAddOns = resolveMatchingAddOns(
            targetedProduct,
            updatedProductList,
            sortAddon
        )
        setAddOnSelection(matchingAddOns)

        let sortedProductList = sortProductListWithAddOns(
            updatedProductList,
            zoneProductList,
            targetedProduct
        )
        const updatedSelectedPackets = dropAddOnsWithoutParentSelected(
            selectedPackets,
            sortedProductList
        )
        if (updatedSelectedPackets.length !== selectedPackets.length) {
            setSelectedPackets(updatedSelectedPackets)
            updatedProductList = updateNbSelectedUnits(
                sortedProductList,
                updatedSelectedPackets
            )
            sortedProductList = sortProductListWithAddOns(
                updatedProductList,
                zoneProductList,
                targetedProduct
            )
        }
        setProductListWithAddons(sortedProductList)
        if (matchingAddOns.length === 0) {
            setSelectedProduct(undefined)
            backActiveSteps()
        }
    }

    const packetSelectionComponent = () => {
        return (
            <div className="enrollment-step-container enrollment-step-container--quantity">
                {selectedProduct && (
                    <PacketSelectionPage
                        selectedPackets={selectedPackets}
                        selectedProduct={selectedProduct}
                        onPacketSelection={setSelectedPackets}
                        handlePacketConfirmation={() =>
                            handlePacketConfirmation()
                        }
                        onBackToProducts={() => {
                            // Qty edits don't refresh nbSelectedUnits; sync
                            // first so orphan add-ons drop when parent sizes
                            // sizes were zeroed before Continue.
                            const syncedProductList = updateNbSelectedUnits(
                                productListWithAddons,
                                selectedPackets
                            )
                            const cleanedPackets =
                                dropAddOnsWithoutParentSelected(
                                    selectedPackets,
                                    syncedProductList
                                )
                            if (
                                cleanedPackets.length !== selectedPackets.length
                            ) {
                                setSelectedPackets(cleanedPackets)
                            }
                            setSelectedProduct(undefined)
                            setProductListWithAddons(
                                updateNbSelectedUnits(
                                    syncedProductList,
                                    cleanedPackets
                                )
                            )
                            backActiveSteps(1)
                        }}
                        isAddOn={selectedProduct.isAddOn}
                        fullGoalItems={fullGoalItems}
                        showDiscountBar={
                            showPackageDiscountBar &&
                            !goalItemsFetchFailed &&
                            !goalItemsFetchPending
                        }
                    />
                )}
            </div>
        )
    }

    const ageFormComponent = () => (
        <AgeInput
            attribute="age"
            nextStepHandler={() => changeActiveSteps()}
            selectedAge={selectedAge}
            setSelectedAge={setSelectedAge}
        />
    )

    const genderFormComponent = () => (
        <GenderForm
            attribute="gender"
            nextStepHandler={() => changeActiveSteps()}
            selectedGender={selectedGender}
            setSelectedGender={setSelectedGender}
            selectedAge={selectedAge as number}
        />
    )

    const groupRoleFormComponent = () => (
        <GroupRoleForm
            attribute="groupRole"
            nextStepHandler={() => changeActiveSteps()}
            selectedGroupRole={selectedGroupRole}
            setSelectedGroupRole={setSelectedGroupRole}
        />
    )

    const firstNameComponent = () => (
        <TextField
            key="firstname"
            attribute="firstname"
            inputValue={inputText}
            onChangeHandler={onChangeHandler}
            onClickHandler={() => changeActiveSteps()}
        />
    )

    const lastNameComponent = () => (
        <TextField
            continueBtnText={isClientModification ? t('modify') : t('continue')}
            key="lastname"
            attribute="lastname"
            inputValue={inputText}
            onChangeHandler={onChangeHandler}
            onClickHandler={() => {
                if (
                    currentClient?.form_type ===
                    constants.FORM_TYPES.MODIFICATION
                ) {
                    handleSubmit()
                } else {
                    changeActiveSteps()
                }
            }}
        />
    )

    const phoneNumberComponent = () => (
        <PhoneNumber
            clientPhone={currentClient?.phone}
            clientCode={currentClient?.client_code}
            isDetailsEdit={isClientModification}
            onOpenExistingClient={openExistingClient}
            onPhoneLookupOutcome={trackPhoneLookup}
            phoneNumberHandler={setPhoneNumberInput}
            phoneNumberInputValue={phoneNumberInput}
            continueButtonText={
                isClientModification ? t('continue') : t('finish')
            }
            userCountry={country}
            handleSubmit={() => {
                if (
                    currentClient?.form_type ===
                    constants.FORM_TYPES.MODIFICATION
                ) {
                    changeActiveSteps()
                } else {
                    handleSubmit(selectedMobileMoneyProvider)
                }
            }}
        />
    )

    const referralsComponent = () => {
        if (!canViewReferralFlow || currentClient) {
            return null
        }

        return (
            <ClientReferrals
                selectedReferrerOption={selectedReferrerOption}
                setSelectedReferrerOption={setSelectedReferrerOption}
                inputText={inputText}
                onChangeHandler={onChangeHandler}
                nextStepHandler={() => changeActiveSteps()}
            />
        )
    }

    const STEP_TO_COMPONENT = {
        [ENROLLMENT_STEPS.PACKAGE_CHOICE]: packageChoiceComponent,
        [ENROLLMENT_STEPS.PRODUCT_SELECTION]: productSelectionComponent,
        [ENROLLMENT_STEPS.PACKET_SELECTION]: packetSelectionComponent,
        [ENROLLMENT_STEPS.REGISTERED_PACKAGES]: registeredPackagesComponent,
        [ENROLLMENT_STEPS.MOBILE_MONEY_AMOUNT]: mobileMoneyAmountComponent,
        [ENROLLMENT_STEPS.MOBILE_MONEY_SELECTION]:
            mobileMoneySelectionComponent,
        [ENROLLMENT_STEPS.AGE]: ageFormComponent,
        [ENROLLMENT_STEPS.GENDER]: genderFormComponent,
        [ENROLLMENT_STEPS.GROUP_ROLE]: groupRoleFormComponent,
        [ENROLLMENT_STEPS.FIRST_NAME]: firstNameComponent,
        [ENROLLMENT_STEPS.LAST_NAME]: lastNameComponent,
        [ENROLLMENT_STEPS.PHONE_NUMBER]: phoneNumberComponent,
        [ENROLLMENT_STEPS.REFERRALS]: referralsComponent
    }

    const buildFlow = (flowType: ENROLLMENT_FLOW_TYPES) => {
        const steps = getVisibleFlowSteps(flowType)
        visibleFlowStepsRef.current = steps
        return steps.map((step) => STEP_TO_COMPONENT[step]()).filter(Boolean)
    }

    const [currentFlow, setCurrentFlow] = useState(
        buildFlow(ENROLLMENT_FLOW_TYPES.ENROLLMENT)
    )

    const handleBeforeUnload = () => {
        // triggered when tab or app is closed
        if (!formCompletedRef.current) {
            sendAbandonedTrackingEvent()
        }
    }

    useEffect(() => {
        if (selectedMobileMoneyProvider) {
            if (currentFlow.length > activeSteps.length) {
                changeActiveSteps()
            } else {
                handleSubmit(selectedMobileMoneyProvider)
            }
        }
    }, [selectedMobileMoneyProvider])

    // Payment can finish via two channels; both may surface the same outcome:
    // - mobilePaymentStatus: Redux polling while the VE stays on this page
    // - mobilePaymentFeedback: ?mobile-payment=success|failure on return from provider
    // - mobilePaymentError without a response: create-payment request failed (terminal)
    // Poll check errors also set mobilePaymentError but leave response/uuid intact —
    // those must not latch Matomo failure or a later SUCCESS cannot be reported.
    // Status/request-error Matomo only after requestMobilePayment (paymentAttemptActiveRef)
    // so leftover Redux from a prior flow cannot emit a phantom outcome on mount.
    // Return-URL feedback is allowed without that gate. paymentOutcomeTrackedRef
    // keeps success/failure to a single Matomo event per attempt.
    // The return URL is today the only outcome signal for a provider payment:
    // Connect's payment status is not updated by the mobile money callback, so
    // polling never reaches success. Once that callback is wired, the polling
    // tab tracks the outcome and the return-URL tracking must go, or the two
    // tabs double-count one attempt.
    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (
            mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS ||
            mobilePaymentStatus === constants.MOBILE_MONEY.FAILURE
        ) {
            setShowMobilePaymentModal(true)
            setShowMobilePaymentSpinner(false)
            if (paymentAttemptActiveRef.current) {
                trackPaymentOutcome(
                    mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS
                        ? constants.ANALYTICS.NAMES.PAYMENT_SUCCESS
                        : constants.ANALYTICS.NAMES.PAYMENT_FAILURE
                )
            }
        }
    }, [mobilePaymentStatus])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (mobilePaymentError || mobilePaymentFeedback) {
            setShowMobilePaymentModal(true)
            setShowMobilePaymentSpinner(false)
            if (
                mobilePaymentError &&
                !mobilePaymentResponse &&
                !mobilePaymentFeedback
            ) {
                if (paymentAttemptActiveRef.current) {
                    trackPaymentOutcome(
                        constants.ANALYTICS.NAMES.PAYMENT_FAILURE
                    )
                }
            } else if (
                mobilePaymentFeedback === constants.MOBILE_MONEY.SUCCESS
            ) {
                trackPaymentOutcome(constants.ANALYTICS.NAMES.PAYMENT_SUCCESS)
            } else if (
                mobilePaymentFeedback === constants.MOBILE_MONEY.FAILURE
            ) {
                trackPaymentOutcome(constants.ANALYTICS.NAMES.PAYMENT_FAILURE)
            }
        }
    }, [mobilePaymentError, mobilePaymentFeedback])

    useEffect(() => {
        const currentStep = getCurrentEnrollmentStep()
        if (
            currentStep === ENROLLMENT_STEPS.MOBILE_MONEY_AMOUNT ||
            currentStep === ENROLLMENT_STEPS.MOBILE_MONEY_SELECTION
        ) {
            trackPaymentStart()
        }
    }, [activeSteps, currentFlowType])

    useEffect(() => {
        trackEnrollmentStartedIfNeeded()
    }, [
        currentFlowType,
        currentClient?.modification,
        currentClient?.form_type,
        hasPreselectedPacketsFromGoalItems
    ])

    useEffect(() => {
        navbarHandler(constants.HIDE_PART_NAVBAR)
        window.addEventListener('beforeunload', handleBeforeUnload)
        clearMobilePaymentHandler()

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            navbarHandler(constants.SHOW_NAVBAR)
            if (!formCompletedRef.current) {
                sendAbandonedTrackingEvent()
            }
            clearMobilePaymentHandler()
        }
    }, [])

    useEffect(() => {
        if (
            !hasInitializedPreselectedPackets &&
            currentClient?.goal_items?.length &&
            productListWithAddons?.length &&
            selectedPackets?.length === 0
        ) {
            const preSelectedPackets = currentClient.goal_items.reduce(
                (selectedPackets: SelectedPacket[], order: Order) => {
                    // Delivered / ve_delivered items are owned by the backend
                    // and not editable; keep them out of the cart so discount
                    // tier math and all_enrollment are not double-counted, and
                    // so filterGoalItems keeps the lifecycle-bearing old row.
                    if (order.delivered || order.ve_delivered) {
                        return selectedPackets
                    }
                    const product = productListWithAddons.find(
                        (product: ProductWithAddOns) =>
                            product.sf_id === order.product.id
                    )
                    const productPackage = product?.product_prices.find(
                        (productPrice: ProductPriceType) =>
                            productPrice.sf_id === order.package_id
                    )
                    if (productPackage && product) {
                        const isAddOn = productPackage.depends_on.length > 0
                        selectedPackets.push({
                            product_id: product.sf_id,
                            pricebook_id: productPackage.pricebook.sf_id,
                            size: productPackage.package_size.toString(),
                            size_display: productPackage.package_size_display,
                            name: product.name,
                            hidePrice: product.hide_price,
                            price: productPackage.price?.toString(),
                            unit: productPackage.units_of_measure,
                            uuid: order.uuid,
                            package_id: order.package_id,
                            image_url: product.image_url,
                            isAddOn
                        })
                    }
                    return selectedPackets
                },
                []
            )
            if (preSelectedPackets.length > 0) {
                const updatedProductList = updateNbSelectedUnits(
                    productListWithAddons,
                    preSelectedPackets
                )
                const selectedProducts = updatedProductList.filter(
                    (product) => product.nbSelectedUnits > 0
                )
                let sortedProductList = [...updatedProductList]
                for (const selectedProduct of selectedProducts) {
                    sortedProductList = sortProductListWithAddOns(
                        sortedProductList,
                        zoneProductList,
                        selectedProduct
                    )
                }
                setProductListWithAddons(sortedProductList)
                setSelectedPackets(preSelectedPackets)
                setHasPreselectedPacketsFromGoalItems(true)
            }
            setHasInitializedPreselectedPackets(true)
        }
    }, [productListWithAddons, hasInitializedPreselectedPackets])

    useEffect(() => {
        setCurrentFlowType(
            findEnrollmentFlowType({
                formType: currentClient?.form_type,
                modification: currentClient?.modification,
                missingClientInfo
            })
        )
    }, [missingClientInfo])

    useEffect(() => {
        setAnimate(true)
        setCurrentFlow(buildFlow(currentFlowType))
    }, [
        currentFlowType,
        activeSteps,
        selectedPackets,
        hasPreselectedPacketsFromGoalItems,
        selectedProduct,
        inputText,
        phoneNumberInput,
        clientCode,
        productListWithAddons,
        showWarningModal,
        selectedAge,
        selectedGender,
        selectedGroupRole,
        country,
        selectedReferrerOption
    ])

    useEffect(() => {
        if (productListData?.length === 0 && !preFetchLoading) {
            productListHandler({ zoneSFID: zone })
        }
    }, [])

    useEffect(() => {
        if (
            !shouldOpenPaymentRedirect(
                mobilePaymentResponse,
                mobilePaymentUuid,
                paymentSkippedRef.current
            )
        ) {
            return
        }
        if (mobilePaymentUuid) {
            startPollingPayment(mobilePaymentUuid)
        }

        window.open(
            mobilePaymentResponse?.redirect_url,
            '_blank',
            'noopener,noreferrer'
        )
    }, [mobilePaymentResponse, mobilePaymentUuid])

    useEffect(() => {
        if (paymentSkippedRef.current || providerPromptOpen) {
            setShowMobilePaymentSpinner(false)
            return
        }
        if (mobilePaymentError) {
            setShowMobilePaymentSpinner(false)
            return
        }
        if (!mobilePaymentLoading) {
            blockInitialLoadingRef.current = false
        }
        const providerSessionOpen =
            Boolean(mobilePaymentResponse?.redirect_url) &&
            isPaymentResultForAttempt(
                mobilePaymentResponse?.uuid,
                mobilePaymentUuid
            ) &&
            mobilePaymentStatus !== constants.MOBILE_MONEY.SUCCESS &&
            mobilePaymentStatus !== constants.MOBILE_MONEY.FAILURE
        const showForRequest =
            mobilePaymentLoading && !blockInitialLoadingRef.current
        setShowMobilePaymentSpinner(showForRequest || providerSessionOpen)
    }, [
        mobilePaymentLoading,
        mobilePaymentResponse,
        mobilePaymentUuid,
        mobilePaymentStatus,
        providerPromptOpen,
        mobilePaymentError
    ])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (mobilePaymentError) {
            setShowMobilePaymentModal(true)
        }
    }, [mobilePaymentError])

    const skipPaymentHandler = () => {
        const providerSessionOpen = Boolean(mobilePaymentResponse?.redirect_url)
        if (providerSessionOpen) {
            setProviderPromptOpen(true)
            setShowMobilePaymentSpinner(false)
            setShowMobilePaymentModal(true)
            return
        }
        paymentSkippedRef.current = true
        stopPollingPayment()
        setShowMobilePaymentSpinner(false)
        setShowMobilePaymentModal(false)
        clearMobilePaymentHandler()
        trackTimedEvent(paymentWaitExitEvent())
        trackCompletedWithoutPayment()
        setShowSuccessModal(true)
    }

    const formTitle = useMemo(() => {
        if (isClientRegistration) {
            return t('enrollment.registration')
        }
        if (isClientModification) {
            return t('modify')
        }
        return t('enrollment.enrollment')
    }, [isClientRegistration, isClientModification])

    /**
     * Drop the last `stepsToRemove` entries from the stepper.
     * Pass a positive count (e.g. `2`), not a negative slice index.
     */
    const backActiveSteps = (stepsToRemove = 1) => {
        if (activeSteps.length === 1) return
        setActiveSteps((prevActiveSteps) => {
            const nextLength = Math.max(
                1,
                prevActiveSteps.length - stepsToRemove
            )
            return prevActiveSteps.slice(0, nextLength)
        })
    }

    const backButtonHandler = () => {
        const currentFlowSteps = getVisibleFlowSteps(currentFlowType)
        const previousStep = currentFlowSteps[activeSteps.length - 2]

        const wouldReturnToPackageChoice =
            previousStep === ENROLLMENT_STEPS.PACKAGE_CHOICE ||
            (currentFlowType ===
                ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITHOUT_PACKAGES &&
                activeSteps.length === 2)

        if (wouldReturnToPackageChoice) {
            return exitHandler()
        }

        const currentStep = currentFlowSteps[activeSteps.length - 1]
        const isOnCardPaymentStep =
            currentFlowType === ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITH_VOUCHER &&
            currentStep === ENROLLMENT_STEPS.REFERRALS

        if (previousStep === ENROLLMENT_STEPS.PACKET_SELECTION) {
            setSelectedProduct(undefined)
            return backActiveSteps(2)
        }

        if (activeSteps.length === 1) return exitHandler()

        if (isOnCardPaymentStep) {
            setCurrentFlowType(ENROLLMENT_FLOW_TYPES.ENROLLMENT)
            return backActiveSteps()
        }

        setSelectedProduct(undefined)
        backActiveSteps()
    }

    const showWarningModalCallback = useCallback(
        (status: boolean) => {
            showWarningModalHandler(status)
        },
        [showWarningModalHandler]
    )

    const exitHandler = () => {
        clearMobilePaymentHandler()
        if (!showWarningModal) {
            showWarningModalCallback(true)
            return
        }
        showWarningModalCallback(false)
        sendAbandonedTrackingEvent()

        if (currentClient?.form_type === 'modification' && !originUrl) {
            return navigate(-1 as To, { replace: true })
        }
        navigate(`${originUrl || '/client-search'}`, { replace: true })
    }

    const warningModal = () => {
        return (
            <Modal
                okClickHandler={exitHandler}
                cancelClickHandler={() => showWarningModalCallback(false)}
                showModal={showWarningModal || false}
                okButtonText={t('yes')}
                cancelButtonText={t('no')}
                data-testid="mark-delivery-modal"
                position={'center'}
                textHeader={t('visitWarningHeader')}
            >
                <WarningModalContent warningText={t('visitWarningBody')} />
            </Modal>
        )
    }

    const showWarningModalMemo = useMemo(() => {
        if (showWarningModal) {
            return warningModal()
        }
    }, [showWarningModal])

    const navigateToNextURL = () => {
        if (nextURL) {
            if (nextURL === '/visit') {
                localStorage.setItem(
                    constants.CLIENT_FOR_VISIT_STORE,
                    JSON.stringify({
                        clientCode: currentClient.client_code,
                        villageId: currentClient.village_id || villageId,
                        clientName: currentClient.firstname,
                        origin: constants.ORIGIN_CLIENT_DETAIL
                    })
                )
            }
            const separator = nextURL.includes('?') ? '&' : '?'
            const navigationFromParams = originUrl
                ? `${separator}from=${originUrl}`
                : ''
            return navigate(`${nextURL}${navigationFromParams}`, {
                replace: true
            })
        }
        const allEnrollment =
            selectedPackets.length > 0
                ? sumRegisteredPackageNets(
                      buildRegisteredPackageRows(
                          selectedPackets,
                          fullGoalItems,
                          productListWithAddons
                      )
                  )
                : currentClient?.all_enrollment
        navigate(
            buildClientUrl(
                {
                    all_enrollment: allEnrollment,
                    client_code: clientCode || currentClient?.client_code,
                    village_id: villageId
                },
                selectedPackets.length > 0
                    ? [{ delivered: false, dropped: false }]
                    : undefined
            ),
            { replace: true }
        )
    }

    const flowStepKeys = getVisibleFlowSteps(currentFlowType)
    visibleFlowStepsRef.current = flowStepKeys
    const currentFlowStep = flowStepKeys[activeSteps.length - 1]

    const enrollmentBaseClasses = buildEnrollmentBaseClasses({
        isFullBleed: isFullBleedEnrollmentStep(
            currentFlowStep,
            selectedProduct
        ),
        isClientRegistration,
        isClientModification
    })

    const {
        isPackageChoiceStep,
        stepCount: stepperStepCount,
        activeSteps: stepperActiveSteps
    } = getStepperDisplay(flowStepKeys, activeSteps, currentFlow.length)

    return (
        <>
            <CSSTransition
                unmountOnExit
                timeout={constants.ANIMATION_TIMEOUT}
                in={animate}
                classNames="generic"
                appear
                nodeRef={nodeRef}
            >
                <div ref={nodeRef}>
                    <div className="sticky-header" id="sticky-header">
                        <Container>
                            <TopBar
                                back={!selectedProduct}
                                backNavigation={backButtonHandler}
                                close={exitHandler}
                            >
                                {formTitle}
                            </TopBar>
                        </Container>
                    </div>
                    {showMobilePaymentSpinner ? (
                        <>
                            <Spinner pageSpinner={true} fullscreen={true} />
                            <div className="payment-wait__skip">
                                <Button
                                    style="outline"
                                    type="button"
                                    onClick={skipPaymentHandler}
                                    id="skip-payment"
                                >
                                    {t('mobileMoney.skipPayment')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Modal
                                showModal={addOnSelection.length > 0}
                                position="bottom"
                                aria-label="packet-addon-selection-modal"
                            >
                                <div className="modal--body-container">
                                    {selectedProduct &&
                                        addOnSelection.length > 0 && (
                                            <PacketSelectionPage
                                                selectedPackets={
                                                    selectedPackets
                                                }
                                                selectedProduct={{
                                                    ...selectedProduct,
                                                    addOns: addOnSelection
                                                }}
                                                onPacketSelection={
                                                    setSelectedPackets
                                                }
                                                handlePacketConfirmation={() => {
                                                    handlePacketConfirmation(
                                                        true
                                                    )
                                                }}
                                                isAddOn={true}
                                            />
                                        )}
                                </div>
                            </Modal>
                            <div className={enrollmentBaseClasses}>
                                {!isPackageChoiceStep && (
                                    <Steps
                                        steps={stepperStepCount}
                                        activeSteps={stepperActiveSteps}
                                    />
                                )}
                                {/* This is where we render the active step */}
                                <div className="enrollment-base--step-content">
                                    {currentFlow[activeSteps.length - 1]}
                                </div>
                                {showSuccessModal && (
                                    <PopupModal
                                        closeModal={() => {
                                            setShowSuccessModal(false)
                                            navigateToNextURL()
                                        }}
                                    >
                                        <SuccessMessage
                                            firstName={inputText.firstname}
                                            clientCategoryForIconColor={getEnrollmentSuccessIconCategory(
                                                isClientRegistration,
                                                isClientModification
                                            )}
                                        />
                                    </PopupModal>
                                )}
                                {showMobilePaymentModal && (
                                    <PopupModal
                                        closeModal={() => {
                                            stopPollingPayment()
                                            setShowMobilePaymentModal(false)
                                            setShowSuccessModal(true)
                                        }}
                                        showCloseIconBtn={
                                            mobilePaymentFeedback ===
                                            constants.MOBILE_MONEY.SUCCESS
                                        }
                                    >
                                        <PaymentFeedbackMessage
                                            amount={paymentAmount}
                                            t={t}
                                            status={
                                                mobilePaymentStatus ||
                                                mobilePaymentFeedback ||
                                                (providerPromptOpen
                                                    ? constants.MOBILE_MONEY
                                                          .PENDING
                                                    : constants.MOBILE_MONEY
                                                          .FAILURE)
                                            }
                                            continueHandler={() => {
                                                const paymentStatus =
                                                    mobilePaymentStatus ||
                                                    mobilePaymentFeedback ||
                                                    constants.MOBILE_MONEY
                                                        .FAILURE
                                                if (
                                                    paymentStatus ===
                                                    constants.MOBILE_MONEY
                                                        .FAILURE
                                                ) {
                                                    trackCompletedWithoutPayment()
                                                }
                                                stopPollingPayment()
                                                clearMobilePaymentHandler()
                                                setShowMobilePaymentModal(false)
                                                setShowSuccessModal(true)
                                            }}
                                            retryHandler={() => {
                                                stopPollingPayment()
                                                setShowMobilePaymentModal(false)
                                                requestMobilePayment(
                                                    currentClient?.client_code ||
                                                        clientCode,
                                                    currentClient?.mobileMoneyProvider ||
                                                        selectedMobileMoneyProvider,
                                                    currentClient?.paymentAmount ||
                                                        paymentAmount
                                                )
                                            }}
                                        />
                                    </PopupModal>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </CSSTransition>
            {existingClientModals}
            {showWarningModalMemo}
        </>
    )
}

export default EnrollmentBase
